import React, { FC, useCallback, useEffect, useState } from 'react';
import { Redirect, Route, Switch, useHistory, useLocation, useRouteMatch } from 'react-router-dom';
import './Login.scss';
import { Auth } from '../../components/loginParts/Auth';
import { authSlice } from '../../store/reducers/AuthSlice';
import { appSlice } from '../../store/reducers/AppSlice';
import { useAppDispatch, useAppSelector } from '../../hooks/redux';
import { ForgotPassword } from '../../components/loginParts/ForgotPassword';
import { PasswordRecoveryAssistance } from '../../components/loginParts/PasswordRecoveryAssistance';
import { RequestCodeSms } from '../../components/loginParts/RequestCodeSms';
import { EnterCodeSms } from '../../components/loginParts/EnterCodeSms';
import { PasswordRecoveryByPhone } from '../../components/loginParts/passwordRecoveryForms/RecoveryByPhone';
import { PasswordRecoveryByEmail } from '../../components/loginParts/passwordRecoveryForms/RecoveryByEmail';
import { GlobalRouteNames, LoginRouteNames } from '../../models/Common';
import { Page404 } from '../404';
import { TwoFactorAuth } from '../../components/loginParts/TwoFactorAuth';

interface LoginForm {
  component: JSX.Element;
  routePath: LoginRouteNames;
  isExact?: boolean;
}

interface LocationState {
  from: {
    pathname: string;
    search: string;
  };
}

export const Login: FC = () => {
  const history = useHistory();
  const dispatch = useAppDispatch();
  const { path, url } = useRouteMatch();
  const { setFormError, setStep } = authSlice.actions;
  const { setLocation } = appSlice.actions;
  const [isPageToTop, setIsPageToTop] = useState(false);
  const { isAuth, logoutIsClicked } = useAppSelector((stateIsAuth) => stateIsAuth.authReducer);
  const { location: locationApp } = useAppSelector((appState) => appState.appReducer);
  const location = useLocation<LocationState>();
  const { state } = location;
  const fromLocation = !state || logoutIsClicked ? { pathname: GlobalRouteNames.HomePage, search: '' } : state.from;

  useEffect(() => {
    dispatch(setLocation({ pathname: fromLocation.pathname, search: fromLocation.search }));
  }, []);

  const handleLink = useCallback(
    (link: LoginRouteNames) => {
      dispatch(setFormError(null));
      history.push(`${url}${link}`);
    },
    [history],
  );

  const handleBack = () => {
    dispatch(setFormError(null));
    history.goBack();
  };

  const handleComplete = () => {
    dispatch(setStep(0));
    history.push(GlobalRouteNames.LoginPage);
  };

  const loginForms: LoginForm[] = [
    {
      component: <Auth handleLink={handleLink} setIsPageToTop={setIsPageToTop} fromLocation={locationApp} />,
      routePath: LoginRouteNames.AuthForm,
      isExact: true,
    },
    {
      component: <PasswordRecoveryAssistance handleLink={handleLink} />,
      routePath: LoginRouteNames.PasswordRecoveryAssistanceForm,
      isExact: true,
    },
    {
      component: <TwoFactorAuth setIsPageToTop={setIsPageToTop} fromLocation={locationApp} />,
      routePath: LoginRouteNames.TwoFactorAuth,
      isExact: true,
    },
    {
      component: <ForgotPassword handleBack={handleBack} handleLink={handleLink} />,
      routePath: LoginRouteNames.ForgotPasswordForm,
      isExact: true,
    },
    {
      component: <EnterCodeSms handleBack={handleBack} />,
      routePath: LoginRouteNames.EnterCodeSmsForm,
      isExact: true,
    },
    {
      component: <RequestCodeSms handleBack={handleBack} handleLink={handleLink} />,
      routePath: LoginRouteNames.RequestCodeSmsForm,
      isExact: true,
    },
    {
      component: <PasswordRecoveryByPhone handleBack={handleBack} handleComplete={handleComplete} />,
      routePath: LoginRouteNames.PasswordRecoveryByPhoneForm,
      isExact: true,
    },
    {
      component: <PasswordRecoveryByEmail handleBack={handleBack} handleComplete={handleComplete} />,
      routePath: LoginRouteNames.PasswordRecoveryByEmailForm,
      isExact: false,
    },
    {
      component: <PasswordRecoveryByEmail handleBack={handleBack} handleComplete={handleComplete} />,
      routePath: LoginRouteNames.PasswordRecoveryByEmailFormWithoutApp,
      isExact: false,
    },
    { component: <Page404 />, routePath: LoginRouteNames.Page404 },
  ];

  const redirectIfAuth = () => {
    return <Redirect push to={GlobalRouteNames.HomePage} />;
  };

  return isAuth ? (
    redirectIfAuth()
  ) : (
    <div className="login-page page">
      <div className={`login-page__container container ${isPageToTop ? 'login-page__container__top-page' : ''}`}>
        <Switch>
          {loginForms.map((form) => {
            return (
              <Route exact={!!form.isExact} path={path.concat(form.routePath)} key={form.routePath}>
                {form.component}
              </Route>
            );
          })}
        </Switch>
      </div>
    </div>
  );
};
